/** @fileoverview Applies app-specific theme variables to rendered screens. */

import { useEffect, useState, type CSSProperties } from 'react';
import { paletteStyle } from './theme';
import { ThemeContainerContext } from './themeContext';
import type { FabrikThemeProps } from './types';

/**
 * Applies the app's theme variables and size preset to its screens and to the page root, and
 * shares its element so dialogs render inside it.
 * @param props class name, app theme and the screens to theme
 */
export default function FabrikTheme({
  className,
  theme,
  children,
  syncDocument = true,
}: FabrikThemeProps) {
  const palette = paletteStyle(theme);
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const size = theme?.size;

  const primaryColor = theme?.primaryColor;

  /*
   * Menus, selects, date pickers and toasts render outside this element, at the page root, so
   * the app colours and size preset are set on the page root too. A preview or hosted page shows
   * only the app, so nothing else on the page changes. index.css holds the size steps.
   */
  useEffect(() => {
    if (!syncDocument) return;
    const root = document.documentElement;
    const rootPalette = Object.entries(paletteStyle({ primaryColor }));
    rootPalette.forEach(([name, value]) => root.style.setProperty(name, value));
    if (size) {
      root.dataset.appSize = size;
    }
    return () => {
      rootPalette.forEach(([name]) => root.style.removeProperty(name));
      delete root.dataset.appSize;
    };
  }, [primaryColor, size, syncDocument]);

  return (
    <div
      ref={setElement}
      className={className}
      data-app-size={size}
      style={
        {
          '--primary': palette['--primary'],
          '--primary-foreground': palette['--primary-foreground'],
          '--secondary': palette['--secondary'],
          '--secondary-foreground': palette['--secondary-foreground'],
          '--background': palette['--background'],
          '--foreground': palette['--foreground'],
          '--card': palette['--card'],
          '--card-foreground': palette['--card-foreground'],
          '--popover': palette['--popover'],
          '--popover-foreground': palette['--popover-foreground'],
          '--muted': palette['--muted'],
          '--muted-foreground': palette['--muted-foreground'],
          '--border': palette['--border'],
          '--input': palette['--input'],
          '--ring': palette['--ring'],
        } as CSSProperties
      }
    >
      <ThemeContainerContext.Provider value={element}>{children}</ThemeContainerContext.Provider>
    </div>
  );
}
